// Generator dodatkowych plansz (40 szt.) - razem z 10 rekordowymi (step.ts) daje 50.
//
// Pomysl: zamiast pisac kazda plansze recznie, opisujemy JAWNYMI parametrami
// (wysokosc stopnia, szerokosc szczeliny, masa skrzynki...) i skladamy z nich
// pelna `ChallengeDefinition`. Dzieki temu:
//  - plansze sa ROZNE, ale mieszcza sie w zakresach sprawdzonych archetypow,
//  - kazda ma przypisany archetyp robota (`ref`), ktory ja przechodzi
//    (test "kazda trase da sie przejsc rozwiazaniem referencyjnym"),
//  - zmiana parametru to jedna liczba, a nie przepisywanie geometrii.
//
// Ten modul nie zna Plancka ani DOM - to czyste dane (jak step.ts).

import type { PartLimits } from '../domain/limits';
import type {
  ChallengeDefinition,
  DynamicProp,
  PropTarget,
  ReferenceRobot,
  StaticBox,
} from './step';

/** Wspolne domyslne wartosci prob (metry, sekundy). */
const GROUND_H = 1;
const GROUND_FRICTION = 0.85;
const OOB = { minX: -12, maxX: 14, minY: -2, maxY: 6 };

interface TrackCommon {
  id: string;
  label: string;
  hint: string;
  budgetPln: number;
  parTimeSec: number;
  limits?: PartLimits;
  timeLimitSec?: number;
  settleTimeSec?: number;
  stuckTimeoutSec?: number;
}

function finishTrack(
  common: TrackCommon,
  geometry: {
    staticBoxes: StaticBox[];
    dynamicProps?: DynamicProp[];
    propTargets?: PropTarget[];
    groundTopY: number;
    startX: number;
    finishX: number;
    platformTopY: number;
    view: { minX: number; maxX: number; centerY: number };
    groundFriction?: number;
    outOfBounds?: { minX: number; maxX: number; minY: number; maxY: number };
  },
): ChallengeDefinition {
  return {
    id: common.id,
    label: common.label,
    hint: common.hint,
    budgetPln: common.budgetPln,
    limits: common.limits,
    parTimeSec: common.parTimeSec,
    staticBoxes: geometry.staticBoxes,
    dynamicProps: geometry.dynamicProps,
    propTargets: geometry.propTargets,
    groundFriction: geometry.groundFriction ?? GROUND_FRICTION,
    groundTopY: geometry.groundTopY,
    startX: geometry.startX,
    finishX: geometry.finishX,
    platformTopY: geometry.platformTopY,
    timeLimitSec: common.timeLimitSec ?? 22,
    settleTimeSec: common.settleTimeSec ?? 1.0,
    stuckTimeoutSec: common.stuckTimeoutSec ?? 3.0,
    outOfBounds: geometry.outOfBounds ?? OOB,
    view: geometry.view,
  };
}

/** Stopien w gore: podloze do `edge`, potem platforma na wysokosci `height`. */
function stepTrack(
  common: TrackCommon,
  params: { height: number; edge: number },
): ChallengeDefinition {
  const { height, edge } = params;
  return finishTrack(common, {
    staticBoxes: [
      { x: (edge - 10) / 2, y: -GROUND_H / 2, w: edge + 10, h: GROUND_H },
      { x: edge + 5, y: height - GROUND_H / 2, w: 10, h: GROUND_H },
    ],
    groundTopY: 0,
    startX: edge - 2.4,
    finishX: edge + 3.5,
    platformTopY: height,
    view: { minX: edge - 3.7, maxX: edge + 5.9, centerY: 0.55 + height * 1.4 },
  });
}

/** Ostry grzbiet (garb): rowek szerokosci `width` jest podniesiony o `height`. */
function ridgeTrack(
  common: TrackCommon,
  params: { height: number; width: number; at: number },
): ChallengeDefinition {
  const { height, width, at } = params;
  const left = at - width / 2;
  const right = at + width / 2;
  return finishTrack(common, {
    staticBoxes: [
      { x: (left - 10) / 2, y: -GROUND_H / 2, w: left + 10, h: GROUND_H },
      { x: at, y: height - GROUND_H / 2, w: width, h: GROUND_H },
      { x: (right + 11) / 2, y: -GROUND_H / 2, w: 11 - right, h: GROUND_H },
    ],
    groundTopY: 0,
    startX: at - 2.9,
    finishX: at + 2.3,
    platformTopY: 0,
    view: { minX: at - 4.2, maxX: at + 5.4, centerY: 0.55 + height },
  });
}

/** Dolina: obnizenie o `depth` na szerokosci `width` (zjazd i wyjazd). */
function valleyTrack(
  common: TrackCommon,
  params: { depth: number; width: number; at: number },
): ChallengeDefinition {
  const { depth, width, at } = params;
  const left = at - width / 2;
  const right = at + width / 2;
  return finishTrack(common, {
    staticBoxes: [
      { x: (left - 10) / 2, y: -GROUND_H / 2, w: left + 10, h: GROUND_H },
      { x: at, y: -depth - GROUND_H / 2, w: width, h: GROUND_H },
      { x: right + 3.5, y: -GROUND_H / 2, w: 7, h: GROUND_H },
    ],
    groundTopY: 0,
    startX: left - 2.4,
    finishX: right + 2.0,
    platformTopY: 0,
    view: { minX: left - 3.7, maxX: right + 2.9, centerY: 0.6 },
  });
}


/** Schody: nizszy stopien `h1`, a potem platforma `h2` (oba "w gore"). */
function stairsTrack(
  common: TrackCommon,
  params: { h1: number; h2: number; step2At: number },
): ChallengeDefinition {
  const { h1, h2, step2At } = params;
  return finishTrack(common, {
    staticBoxes: [
      { x: (step2At - 10) / 2, y: -GROUND_H / 2, w: step2At + 10, h: GROUND_H },
      { x: step2At / 2, y: h1 - GROUND_H / 2, w: step2At, h: GROUND_H },
      { x: step2At + 4.25, y: h2 - GROUND_H / 2, w: 8.5, h: GROUND_H },
    ],
    groundTopY: 0,
    startX: -2.4,
    finishX: step2At + 3.0,
    platformTopY: h2,
    view: { minX: -3.7, maxX: step2At + 3.9, centerY: 0.6 + h2 },
  });
}

/** Szczelina bez podloza (miedzy `from` i `to`) - przejezdza tylko duze kolo. */
function gapTrack(
  common: TrackCommon,
  params: { from: number; to: number },
): ChallengeDefinition {
  const { from, to } = params;
  return finishTrack(common, {
    staticBoxes: [
      { x: (from - 10) / 2, y: -GROUND_H / 2, w: from + 10, h: GROUND_H },
      { x: (to + 11) / 2, y: -GROUND_H / 2, w: 11 - to, h: GROUND_H },
    ],
    groundTopY: 0,
    startX: from - 2.4,
    finishX: to + 2.4,
    platformTopY: 0,
    // Wpadniecie do szczeliny to wypad poza obszar (na dole nie ma podloza).
    outOfBounds: { minX: -12, maxX: 14, minY: -1.2, maxY: 6 },
    view: { minX: from - 3.7, maxX: to + 3.9, centerY: 0.6 },
  });
}

/** Popychanie luznej skrzynki: musi dotrzec za `targetMinX`. */
function crateTrack(
  common: TrackCommon,
  params: { massKg: number; crateAt: number; targetMinX: number },
): ChallengeDefinition {
  const { massKg, crateAt, targetMinX } = params;
  return finishTrack({ ...common, timeLimitSec: 25, stuckTimeoutSec: 4.0 }, {
    staticBoxes: [{ x: 0, y: -GROUND_H / 2, w: 24, h: GROUND_H }],
    dynamicProps: [
      { id: 'crate', x: crateAt, y: 0.11, w: 0.5, h: 0.22, massKg, friction: 0.4 },
    ],
    propTargets: [{ propId: 'crate', minX: targetMinX }],
    groundTopY: 0,
    startX: -2.4,
    finishX: targetMinX - 0.2,
    platformTopY: 0,
    view: { minX: -3.7, maxX: targetMinX + 2.6, centerY: 0.6 },
  });
}

/** Przepchniecie ciezkiego ciezarU lezacego na drodze (masa `massKg`). */
function pushTrack(
  common: TrackCommon,
  params: { massKg: number; boxW: number; boxAt: number },
): ChallengeDefinition {
  const { massKg, boxW, boxAt } = params;
  const boxH = 0.22;
  return finishTrack({ ...common, timeLimitSec: 25, stuckTimeoutSec: 3.5 }, {
    staticBoxes: [{ x: 0, y: -GROUND_H / 2, w: 24, h: GROUND_H }],
    dynamicProps: [
      { id: 'weight', x: boxAt, y: boxH / 2, w: boxW, h: boxH, massKg, friction: 0.5 },
    ],
    groundTopY: 0,
    startX: -2.4,
    finishX: boxAt + 2.7,
    platformTopY: 0,
    groundFriction: 0.9,
    view: { minX: -3.7, maxX: boxAt + 3.6, centerY: 0.6 },
  });
}

/** Ruchoma kladka: deska lezy nad przepascia i przesuwa sie pod robotem. */
function plankTrack(
  common: TrackCommon,
  params: { from: number; to: number; plankW: number },
): ChallengeDefinition {
  const { from, to, plankW } = params;
  return finishTrack(common, {
    staticBoxes: [
      { x: -5, y: -GROUND_H / 2, w: 10, h: GROUND_H },
      { x: (to + 11) / 2, y: -GROUND_H / 2, w: 11 - to, h: GROUND_H },
    ],
    dynamicProps: [
      {
        id: 'plank',
        x: (from + to) / 2,
        y: 0.05,
        w: plankW,
        h: 0.1,
        massKg: 2.5,
        friction: 0.95,
      },
    ],
    groundTopY: 0,
    startX: -2.4,
    finishX: to + 2.3,
    platformTopY: 0,
    outOfBounds: { minX: -12, maxX: 14, minY: -1.2, maxY: 6 },
    view: { minX: -3.7, maxX: to + 3.9, centerY: 0.6 },
  });
}

/** Hustawka: kladka na przegubie nad przepascia (przechyla sie pod robotem). */
function seesawTrack(
  common: TrackCommon,
  params: { from: number; to: number; span: number },
): ChallengeDefinition {
  const { from, to, span } = params;
  return finishTrack(common, {
    staticBoxes: [
      { x: -5, y: -GROUND_H / 2, w: 10, h: GROUND_H },
      { x: (to + 11) / 2, y: -GROUND_H / 2, w: 11 - to, h: GROUND_H },
    ],
    dynamicProps: [
      {
        id: 'seesaw',
        x: (from + to) / 2,
        y: 0.15,
        w: span,
        h: 0.1,
        massKg: 3,
        friction: 0.9,
        pivot: { x: 0, y: 0 },
      },
    ],
    groundTopY: 0,
    startX: -2.4,
    finishX: to + 2.0,
    platformTopY: 0,
    outOfBounds: { minX: -12, maxX: 14, minY: -1.2, maxY: 6 },
    view: { minX: -3.7, maxX: to + 3.9, centerY: 0.7 },
  });
}

interface ExtraSpec {
  /** Archetyp robota, ktory przechodzi te trase (do testow i podgladow). */
  ref: ReferenceRobot;
  track: ChallengeDefinition;
}

const EXTRA_SPECS: readonly ExtraSpec[] = [
  // --- Stopnie (wyzsze niz w step-v1, ale w zakresie kol ogromnych) ---
  {
    ref: 'good',
    track: stepTrack(
      { id: 'step-h16', label: 'Stopien 0,16 m', hint: 'Stopien nieco wyzszy - nadal wystarczaja duze kola.', budgetPln: 45, parTimeSec: 5.0 },
      { height: 0.16, edge: 0.6 },
    ),
  },
  {
    ref: 'huge',
    track: stepTrack(
      { id: 'step-h18', label: 'Stopien 0,18 m', hint: 'Wyzej niz promien duzego kola - potrzebne kolo ogromne.', budgetPln: 70, parTimeSec: 5.5 },
      { height: 0.18, edge: 0.5 },
    ),
  },
  {
    ref: 'huge',
    track: stepTrack(
      { id: 'step-h20', label: 'Stopien 0,20 m', hint: 'Wysoki prog - kolo ogromne i mocny naped.', budgetPln: 70, parTimeSec: 5.5 },
      { height: 0.2, edge: 0.7 },
    ),
  },
  {
    ref: 'huge',
    track: stepTrack(
      { id: 'step-h24', label: 'Stopien 0,24 m', hint: 'Prawie krawedz - potrzebny zapas momentu.', budgetPln: 70, parTimeSec: 6.0 },
      { height: 0.24, edge: 0.4 },
    ),
  },
  // --- Grzbiety ---
  {
    ref: 'good',
    track: ridgeTrack(
      { id: 'ridge-w15', label: 'Grzbiet 0,12 m', hint: 'Garb w podlozu - wjazd i zjazd.', budgetPln: 45, parTimeSec: 5.0 },
      { height: 0.12, width: 1.5, at: 0.6 },
    ),
  },
  {
    ref: 'good',
    track: ridgeTrack(
      { id: 'ridge-w20', label: 'Grzbiet 0,10 m', hint: 'Szeroki, niski garb - uwaga na srodek masy.', budgetPln: 45, parTimeSec: 5.0 },
      { height: 0.1, width: 2.0, at: 0.8 },
    ),
  },
  {
    ref: 'good',
    track: ridgeTrack(
      { id: 'ridge-w12', label: 'Grzbiet 0,14 m', hint: 'Stromy garb - trzeba sie rozpedzic.', budgetPln: 50, parTimeSec: 5.5 },
      { height: 0.14, width: 1.2, at: 0.5 },
    ),
  },
  {
    ref: 'good',
    track: ridgeTrack(
      { id: 'ridge-w30', label: 'Grzbiet 0,08 m szeroki', hint: 'Dlugi, lagodny garb - tylko dwa kola w budzecie.', budgetPln: 45, parTimeSec: 5.5, limits: { categories: { wheel: 2 } } },
      { height: 0.08, width: 3.0, at: 1.0 },
    ),
  },
  {
    ref: 'good',
    track: ridgeTrack(
      { id: 'ridge-w18', label: 'Grzbiet 0,12 m szeroki', hint: 'Garb szerszy niz rozstaw kol.', budgetPln: 50, parTimeSec: 5.5 },
      { height: 0.12, width: 1.8, at: 0.7 },
    ),
  },
  // --- Doliny ---
  {
    ref: 'good',
    track: valleyTrack(
      { id: 'valley-d10', label: 'Dolina 0,10 m', hint: 'Plytkie obnizenie - zjazd i wyjazd.', budgetPln: 50, parTimeSec: 5.0 },
      { depth: 0.1, width: 2.0, at: 1.0 },
    ),
  },
  {
    ref: 'good',
    track: valleyTrack(
      { id: 'valley-d12', label: 'Dolina 0,12 m', hint: 'Obnizenie i powrot na gore.', budgetPln: 50, parTimeSec: 5.5 },
      { depth: 0.12, width: 3.0, at: 1.5 },
    ),
  },
  {
    ref: 'good',
    track: valleyTrack(
      { id: 'valley-d16', label: 'Dolina 0,16 m', hint: 'Gleboka dolina - potrzebny zapas momentu.', budgetPln: 55, parTimeSec: 6.0 },
      { depth: 0.16, width: 3.0, at: 1.2 },
    ),
  },
  {
    ref: 'good',
    track: valleyTrack(
      { id: 'valley-w40', label: 'Dolina 0,14 m szeroka', hint: 'Szeroka dolina - robot rozpedza sie na dnie.', budgetPln: 55, parTimeSec: 6.0 },
      { depth: 0.14, width: 4.0, at: 1.5 },
    ),
  },
  {
    ref: 'good',
    track: valleyTrack(
      { id: 'valley-d18', label: 'Dolina 0,18 m', hint: 'Najglebsza dolina - wyjazd wymaga kola ogromnego.', budgetPln: 60, parTimeSec: 6.5 },
      { depth: 0.18, width: 2.5, at: 1.0 },
    ),
  },
  // --- Schody ---
  {
    ref: 'good',
    track: stairsTrack(
      { id: 'stairs-10-20', label: 'Schody 0,10 + 0,10 m', hint: 'Dwa niskie stopnie pod rzad.', budgetPln: 50, parTimeSec: 5.5 },
      { h1: 0.1, h2: 0.2, step2At: 1.2 },
    ),
  },
  {
    ref: 'good',
    track: stairsTrack(
      { id: 'stairs-12-24', label: 'Schody 0,12 + 0,12 m', hint: 'Dwa stopnie - kazdy osobno niski.', budgetPln: 55, parTimeSec: 6.0 },
      { h1: 0.12, h2: 0.24, step2At: 1.4 },
    ),
  },
  {
    ref: 'good',
    track: stairsTrack(
      { id: 'stairs-14-28', label: 'Schody 0,14 + 0,14 m', hint: 'Klasyczne schody - wjazd i wyjazd.', budgetPln: 55, parTimeSec: 6.0 },
      { h1: 0.14, h2: 0.28, step2At: 1.6 },
    ),
  },
  {
    ref: 'good',
    track: stairsTrack(
      { id: 'stairs-10-26', label: 'Schody 0,10 + 0,16 m', hint: 'Drugi stopien wyzszy - potrzebny zapas momentu.', budgetPln: 60, parTimeSec: 6.5 },
      { h1: 0.1, h2: 0.26, step2At: 1.2 },
    ),
  },
  // --- Szczeliny (tylko kolo ogromne) ---
  {
    ref: 'huge',
    track: gapTrack(
      { id: 'gap-45', label: 'Szczelina 0,45 m', hint: 'Przerwa w podlozu - kolo ogromne przejezdza.', budgetPln: 70, parTimeSec: 5.0 },
      { from: 0, to: 0.45 },
    ),
  },
  {
    ref: 'huge',
    track: gapTrack(
      { id: 'gap-50', label: 'Szczelina 0,50 m', hint: 'Szersza przerwa - tylko duze kolo.', budgetPln: 70, parTimeSec: 5.0 },
      { from: 0, to: 0.5 },
    ),
  },
  // --- Skrzynki do przewiezienia (niski lazik z malymi kolami) ---
  {
    ref: 'small',
    track: crateTrack(
      { id: 'crate-m10', label: 'Skrzynka 1,0 kg', hint: 'Popchnij skrzynke za mete - niski lazik.', budgetPln: 45, parTimeSec: 7.5 },
      { massKg: 1.0, crateAt: 0.6, targetMinX: 3.5 },
    ),
  },
  {
    ref: 'small',
    track: crateTrack(
      { id: 'crate-m15', label: 'Skrzynka 1,5 kg', hint: 'Ciezsza skrzynka - trzeba ja pchac.', budgetPln: 50, parTimeSec: 8.5 },
      { massKg: 1.5, crateAt: 0.6, targetMinX: 3.5 },
    ),
  },
  {
    ref: 'small',
    track: crateTrack(
      { id: 'crate-far', label: 'Skrzynka dalej', hint: 'Dluzsza droga ze skrzynka - bez duzych kol.', budgetPln: 55, parTimeSec: 9.0, limits: { types: { wheel_huge: 0 } } },
      { massKg: 1.2, crateAt: 0.6, targetMinX: 4.5 },
    ),
  },
  {
    ref: 'small',
    track: crateTrack(
      { id: 'crate-m20', label: 'Skrzynka 2,0 kg', hint: 'Ciezka skrzynka - potrzebna przyczepnosc.', budgetPln: 55, parTimeSec: 9.5 },
      { massKg: 2.0, crateAt: 0.6, targetMinX: 3.5 },
    ),
  },
  {
    ref: 'small',
    track: crateTrack(
      { id: 'crate-m08', label: 'Skrzynka 0,8 kg', hint: 'Lekka skrzynka - szybkie przepchniecie.', budgetPln: 45, parTimeSec: 7.0 },
      { massKg: 0.8, crateAt: 0.6, targetMinX: 3.2 },
    ),
  },
  {
    ref: 'fast',
    track: gapTrack(
      { id: 'gap-55', label: 'Szczelina 0,55 m', hint: 'Szczelina dla szybkiego kola - bez malych kol.', budgetPln: 60, parTimeSec: 5.0, limits: { types: { wheel_small: 0 } } },
      { from: 0, to: 0.55 },
    ),
  },
  {
    ref: 'huge',
    track: gapTrack(
      { id: 'gap-70', label: 'Szczelina 0,70 m', hint: 'Najszersza szczelina - tylko kolo ogromne.', budgetPln: 70, parTimeSec: 6.0 },
      { from: 0, to: 0.7 },
    ),
  },
  // --- Przepychanie ciezarow (niski lazik z malymi kolami) ---
  {
    ref: 'small',
    track: pushTrack(
      { id: 'push-m30', label: 'Ciezar 3,0 kg', hint: 'Ciezar blokuje droge - przepchnij go.', budgetPln: 60, parTimeSec: 7.5 },
      { massKg: 3.0, boxW: 0.8, boxAt: 0.8 },
    ),
  },
  {
    ref: 'small',
    track: pushTrack(
      { id: 'push-m35', label: 'Ciezar 3,5 kg', hint: 'Ciezszy ciezar - potrzebna przyczepnosc.', budgetPln: 65, parTimeSec: 8.0 },
      { massKg: 3.5, boxW: 0.8, boxAt: 0.8 },
    ),
  },
  {
    ref: 'small',
    track: pushTrack(
      { id: 'push-m50', label: 'Ciezar 5,0 kg', hint: 'Bardzo ciezki ciezar - dwa moduly napedu.', budgetPln: 70, parTimeSec: 9.0, limits: { categories: { drive: 2 } } },
      { massKg: 5.0, boxW: 0.8, boxAt: 0.8 },
    ),
  },
  {
    ref: 'small',
    track: pushTrack(
      { id: 'push-wide', label: 'Ciezar szeroki', hint: 'Szeroki ciezar - latwiej go przewrocic.', budgetPln: 70, parTimeSec: 8.5 },
      { massKg: 4.0, boxW: 1.0, boxAt: 0.9 },
    ),
  },
  // --- Ruchome kladki ---
  {
    ref: 'good',
    track: plankTrack(
      { id: 'plank-06', label: 'Kladka 0,6 m', hint: 'Przejedz po ruchomej kladce.', budgetPln: 55, parTimeSec: 6.0 },
      { from: 0, to: 0.6, plankW: 1.1 },
    ),
  },
  {
    ref: 'good',
    track: plankTrack(
      { id: 'plank-08', label: 'Kladka 0,8 m', hint: 'Szersza przepasc - dluzsza kladka.', budgetPln: 55, parTimeSec: 6.5 },
      { from: 0, to: 0.8, plankW: 1.4 },
    ),
  },
  {
    ref: 'good',
    track: plankTrack(
      { id: 'plank-10', label: 'Kladka 1,0 m', hint: 'Dluga przepasc - dluga kladka.', budgetPln: 60, parTimeSec: 7.0 },
      { from: 0, to: 1.0, plankW: 1.6 },
    ),
  },
  {
    ref: 'good',
    track: plankTrack(
      { id: 'plank-07', label: 'Kladka 0,7 m', hint: 'Kladka przesunieta - uwaga na krawedzie.', budgetPln: 55, parTimeSec: 6.5 },
      { from: 0.2, to: 0.9, plankW: 1.3 },
    ),
  },
  // --- Hustawki (kladka na przegubie) ---
  {
    ref: 'good',
    track: seesawTrack(
      { id: 'seesaw-14', label: 'Hustawka 1,4 m', hint: 'Krotka hustawka - szybkie przejscie.', budgetPln: 55, parTimeSec: 6.0 },
      { from: 0, to: 0.9, span: 1.4 },
    ),
  },
  {
    ref: 'good',
    track: seesawTrack(
      { id: 'seesaw-16', label: 'Hustawka 1,6 m', hint: 'Kladka przechyli sie pod robotem.', budgetPln: 55, parTimeSec: 6.5 },
      { from: 0, to: 1.0, span: 1.6 },
    ),
  },
  {
    ref: 'good',
    track: seesawTrack(
      { id: 'seesaw-18', label: 'Hustawka 1,8 m', hint: 'Dluzsza hustawka - dluzej sie przechyla.', budgetPln: 55, parTimeSec: 7.0 },
      { from: 0.1, to: 1.2, span: 1.8 },
    ),
  },
  {
    ref: 'good',
    track: seesawTrack(
      { id: 'seesaw-20', label: 'Hustawka 2,0 m', hint: 'Szeroka przepasc i dluga kladka.', budgetPln: 60, parTimeSec: 7.0 },
      { from: 0, to: 1.3, span: 2.0 },
    ),
  },
  {
    ref: 'good',
    track: seesawTrack(
      { id: 'seesaw-22', label: 'Hustawka 2,2 m', hint: 'Najdluzsza hustawka - potrzebny rozped.', budgetPln: 60, parTimeSec: 7.5 },
      { from: -0.1, to: 1.5, span: 2.2 },
    ),
  },
];

/**
 * Trasy dodatkowe (40) - razem z 10 z `step.ts` daje 50 plansz.
 * Kolejnosc w tej tablicy = kolejnosc na mapie podrozy.
 */
export const EXTRA_CHALLENGES: readonly ChallengeDefinition[] = EXTRA_SPECS.map(
  (spec) => spec.track,
);

/**
 * Ktory archetyp robota przechodzi dana trase (tylko testy i podglady) - na tej
 * podstawie `samples.ts` buduje `REFERENCE_SOLUTIONS`.
 */
export const EXTRA_REFERENCES: Readonly<Record<string, ReferenceRobot>> =
  Object.fromEntries(EXTRA_SPECS.map((spec) => [spec.track.id, spec.ref]));
